import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Pencil, Check, X, Trash2, Camera, Loader2 } from 'lucide-react'
import { api } from '../lib/api'

// Sortable column header — click toggles asc/desc, default asc; shows arrow when active.
function SortTh({ field, label, sort, onSort, align = 'left' }: { field: string; label: string; sort: string; onSort: (f: string) => void; align?: 'left' | 'right' }) {
  const [activeField, dir] = sort.split(':')
  const active = activeField === field
  const ChevronUp = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
  const ChevronDown = (props: any) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
  return (
    <th className={`font-medium px-4 py-2.5 select-none cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 ${align === 'right' ? 'text-right' : 'text-left'}`} onClick={() => onSort(field)}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''} ${active ? 'text-brand-600 dark:text-brand-300' : ''}`}>
        {label}
        {active && (dir === 'desc' ? <ChevronDown style={{ width: 12, height: 12 }} /> : <ChevronUp style={{ width: 12, height: 12 }} />)}
      </span>
    </th>
  )
}

export function PatientsPage() {
  const [patients, setPatients] = useState<any[]>([])
  const [totalPatients, setTotalPatients] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const PAGE = 50
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(() => localStorage.getItem('scribey_patients_sort') || 'last_name')
  // Toggle a column's sort: same field flips dir; new field starts ascending.
  function toggleSort(field: string) {
    const [f, d] = sort.split(':')
    setSort(f === field && d !== 'desc' ? `${field}:desc` : field)
  }
  const [showAdd, setShowAdd] = useState(false)
  const [newLastName, setNewLastName] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newDob, setNewDob] = useState('')
  const [adding, setAdding] = useState(false)
  const [scanning, setScanning] = useState(false)
  const scanInputRef = useRef<HTMLInputElement>(null)

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLastName, setEditLastName] = useState('')
  const [editFirstName, setEditFirstName] = useState('')
  const [editDob, setEditDob] = useState('')
  const [saving, setSaving] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    loadPatients()
  }, [query, sort])

  useEffect(() => {
    try { localStorage.setItem('scribey_patients_sort', sort) } catch {}
  }, [sort])

  async function loadPatients() {
    try {
      const data = await api.listPatients(query || undefined, PAGE, sort, 0)
      setPatients(data.patients)
      setTotalPatients(data.total ?? data.patients.length)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function loadMorePatients() {
    if (loadingMore) return
    setLoadingMore(true)
    try {
      const data = await api.listPatients(query || undefined, PAGE, sort, patients.length)
      setPatients((prev) => {
        const seen = new Set(prev.map((p) => p.id))
        return [...prev, ...data.patients.filter((p: any) => !seen.has(p.id))]
      })
      setTotalPatients(data.total ?? patients.length + data.patients.length)
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingMore(false)
    }
  }

  async function handleAdd() {
    if (!newLastName.trim() || !newFirstName.trim()) return
    setAdding(true)
    try {
      await api.createPatient({
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        dob: newDob || undefined,
      })
      setNewLastName('')
      setNewFirstName('')
      setNewDob('')
      setShowAdd(false)
      loadPatients()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setAdding(false)
    }
  }

  async function handleScan(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setScanning(true)
    try {
      const reader = new FileReader()
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const result = await api.scanPatientImage(base64)
      if (result.last_name) setNewLastName(result.last_name)
      if (result.first_name) setNewFirstName(result.first_name)
      if (result.dob) setNewDob(result.dob)
      setShowAdd(true)
    } catch (err: any) {
      alert('Could not read patient info from image. Try a clearer photo.')
    } finally {
      setScanning(false)
      if (scanInputRef.current) scanInputRef.current.value = ''
    }
  }

  function startEdit(patient: any) {
    setEditingId(patient.id)
    setEditLastName(patient.last_name || '')
    setEditFirstName(patient.first_name || '')
    setEditDob(patient.dob || '')
  }

  async function saveEdit() {
    if (!editingId) return
    setSaving(true)
    try {
      await api.updatePatient(editingId, {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        dob: editDob || null,
      })
      setEditingId(null)
      loadPatients()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Hidden file input for camera scan */}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleScan}
        className="hidden"
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Patients</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scanInputRef.current?.click()}
            disabled={scanning}
            className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            title="Scan patient sticker or EMR screen"
          >
            <Camera size={16} />
            {scanning ? 'Scanning...' : 'Scan'}
          </button>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
        >
          + New Patient
        </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Last Name"
              value={newLastName}
              onChange={(e) => setNewLastName(e.target.value)}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <input
              type="text"
              placeholder="First Name"
              value={newFirstName}
              onChange={(e) => setNewFirstName(e.target.value)}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <input
            type="date"
            placeholder="DOB"
            value={newDob}
            onChange={(e) => setNewDob(e.target.value)}
            className="w-full bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={adding}
              className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {adding ? 'Adding...' : 'Add Patient'}
            </button>
            <button
              onClick={() => setShowAdd(false)}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          placeholder="Search patients..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-3 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort patients"
          className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-3 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 sm:w-auto"
        >
          <option value="last_name">Last name (A–Z)</option>
          <option value="last_name:desc">Last name (Z–A)</option>
          <option value="dob">DOB (oldest first)</option>
          <option value="dob:desc">DOB (youngest first)</option>
          <option value="created_at:desc">Recently added</option>
          <option value="created_at">First added</option>
        </select>
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[0, 1, 2].map(i => (
            <div key={i} className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 h-16" />
          ))}
        </div>
      ) : patients.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-brand-500" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0 .656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          </div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1.5">No patients yet</h3>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-5 max-w-xs mx-auto">Add your first patient manually, or scan an insurance card or EMR screen to import one instantly.</p>
        </div>
      ) : (
        <>
        {/* Desktop table */}
        <div className="hidden md:block bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-200/60 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide border-b border-gray-200 dark:border-gray-800">
              <tr>
                <SortTh field="last_name" label="Last name" sort={sort} onSort={toggleSort} />
                <SortTh field="first_name" label="First name" sort={sort} onSort={toggleSort} />
                <th className="text-left font-medium px-4 py-2.5">Age / Sex</th>
                <SortTh field="dob" label="DOB" sort={sort} onSort={toggleSort} />
                <SortTh field="mrn" label="MRN" sort={sort} onSort={toggleSort} />
                <SortTh field="visit_count" label="Visits" sort={sort} onSort={toggleSort} align="right" />
                <SortTh field="last_visit" label="Last visit" sort={sort} onSort={toggleSort} />
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {patients.map((p) => {
                const age = p.dob ? Math.floor((Date.now() - new Date(p.dob).getTime()) / 31557600000) : null
                const go = () => navigate(`/patients/${p.id}`)
                const cell = "px-4 py-2.5 cursor-pointer"
                return (
                  <tr key={p.id} className="hover:bg-gray-200/60 dark:hover:bg-gray-800/50 group">
                    <td className={cell} onClick={go}>
                      <div className="flex items-center gap-2.5">
                        <div className="shrink-0 w-7 h-7 rounded-full bg-brand-500/15 text-brand-600 dark:text-brand-300 flex items-center justify-center text-[11px] font-semibold">
                          {`${(p.first_name||'')[0]||''}${(p.last_name||'')[0]||''}`.toUpperCase() || '?'}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{p.last_name || '—'}</span>
                      </div>
                    </td>
                    <td className={cell + " text-gray-700 dark:text-gray-300"} onClick={go}>{p.first_name || '—'}</td>
                    <td className={cell + " text-gray-500 dark:text-gray-400"} onClick={go}>{age != null ? `${age}y` : '—'}{p.sex ? ` · ${String(p.sex)[0].toUpperCase()}` : ''}</td>
                    <td className={cell + " text-gray-500 dark:text-gray-400"} onClick={go}>{p.dob || '—'}</td>
                    <td className={cell + " text-gray-500 dark:text-gray-400"} onClick={go}>{p.mrn || '—'}</td>
                    <td className={cell + " text-right text-gray-700 dark:text-gray-300"} onClick={go}>{p.visit_count || 0}</td>
                    <td className={cell + " text-gray-500 dark:text-gray-400"} onClick={go}>{p.last_visit || '—'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); startEdit(p) }} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" title="Edit patient"><Pencil size={14} /></button>
                        <button onClick={async (e) => { e.stopPropagation(); if (!confirm(`Delete ${p.last_name || ''}, ${p.first_name || ''}? Their encounters will be kept but unlinked.`)) return; try { await api.deletePatient(p.id); setPatients(prev => prev.filter(pt => pt.id !== p.id)) } catch (err) { console.error(err) } }} className="p-1.5 text-gray-400 hover:text-red-500" title="Delete patient"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {patients.map((p) => (
            <div
              key={p.id}
              className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
            >
              {editingId === p.id ? (
                /* Edit mode */
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      placeholder="Last Name"
                      className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      placeholder="First Name"
                      className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <input
                      type="date"
                      value={editDob}
                      onChange={(e) => setEditDob(e.target.value)}
                      className="flex-1 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                    {editDob && (
                      <button
                        onClick={() => setEditDob('')}
                        className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-1.5 py-1 shrink-0"
                        title="Clear DOB"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50"
                    >
                      <Check size={14} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <div
                  className="flex items-center justify-between cursor-pointer gap-3"
                  onClick={() => navigate(`/patients/${p.id}`)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Initials avatar */}
                    <div className="shrink-0 w-9 h-9 rounded-full bg-brand-500/15 text-brand-600 dark:text-brand-300 flex items-center justify-center text-xs font-semibold">
                      {`${(p.first_name||'')[0]||''}${(p.last_name||'')[0]||''}`.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-gray-900 dark:text-gray-100 font-medium truncate">
                        {p.last_name && p.first_name ? `${p.last_name}, ${p.first_name}` : p.last_name || p.first_name || p.name || 'Unknown'}
                      </p>
                      {/* Demographics + visit summary */}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {(() => {
                          const bits: any[] = []
                          if (p.dob) {
                            const age = Math.floor((Date.now() - new Date(p.dob).getTime()) / 31557600000)
                            bits.push(<span key="age">{age}y</span>)
                          }
                          if (p.sex) bits.push(<span key="sex" className="uppercase">{String(p.sex)[0]}</span>)
                          if (p.dob) bits.push(<span key="dob">DOB {p.dob}</span>)
                          if (p.mrn) bits.push(<span key="mrn">MRN {p.mrn}</span>)
                          return bits.length ? bits.reduce((acc: any[], el, i) => i === 0 ? [el] : [...acc, <span key={'s'+i} className="text-gray-300 dark:text-gray-700">·</span>, el], []) : <span className="italic text-gray-400 dark:text-gray-600">No DOB on file</span>
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Right: visit summary */}
                  <div className="hidden sm:flex flex-col items-end shrink-0 text-right mr-1">
                    {p.visit_count > 0 ? (
                      <>
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{p.visit_count} visit{p.visit_count === 1 ? '' : 's'}</span>
                        {p.last_visit && <span className="text-[11px] text-gray-400 dark:text-gray-500">last {p.last_visit}</span>}
                      </>
                    ) : (
                      <span className="text-[11px] text-gray-400 dark:text-gray-600 italic">no visits</span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0 ml-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); startEdit(p) }}
                      className="p-1.5 text-gray-300 dark:text-gray-600 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      title="Edit patient"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (!confirm(`Delete ${p.last_name || ''}, ${p.first_name || ''}? Their encounters will be kept but unlinked.`)) return
                        try {
                          await api.deletePatient(p.id)
                          setPatients(prev => prev.filter(pt => pt.id !== p.id))
                        } catch (err) {
                          console.error('Failed to delete patient:', err)
                        }
                      }}
                      className="p-1.5 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      title="Delete patient"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Load more — shared across table + cards */}
        {patients.length < totalPatients && (
          <div className="flex flex-col items-center gap-1 pt-3 pb-2">
            <button
              onClick={loadMorePatients}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50"
            >
              {loadingMore ? <Loader2 size={15} className="animate-spin" /> : null}
              {loadingMore ? 'Loading…' : 'Load more patients'}
            </button>
            <span className="text-[11px] text-gray-400 dark:text-gray-500">Showing {patients.length} of {totalPatients}</span>
          </div>
        )}
        </>
      )}
    </div>
  )
}
